import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, GitBranch, Loader2 } from 'lucide-react';
import {
  useBranchOptimizationRun,
  type ForecastMode,
  type OptimizationRunDetail,
} from '@/hooks/optimization/useOptimizationRuns';

interface WhatIfDialogProps {
  parentRun: OptimizationRunDetail;
  open: boolean;
  onClose: () => void;
}

interface Weights {
  fot: number;
  understaffing: number;
  overtime: number;
  fairness: number;
}

function readWeights(parent: OptimizationRunDetail): Weights {
  const w = (parent.objective_weights ?? {}) as Record<string, unknown>;
  const num = (v: unknown, fb: number) => (typeof v === 'number' ? v : fb);
  return {
    fot: num(w.fot, 1),
    understaffing: num(w.understaffing, 10000),
    overtime: num(w.overtime, 1000),
    fairness: num(w.fairness, 20000),
  };
}

function readForecastMode(parent: OptimizationRunDetail): ForecastMode {
  const inp = (parent.input_snapshot ?? {}) as { forecast_mode?: string };
  const m = inp.forecast_mode;
  return m === 'conservative' || m === 'optimistic' ? m : 'expected';
}

export const WhatIfDialog: React.FC<WhatIfDialogProps> = ({
  parentRun,
  open,
  onClose,
}) => {
  const initial = useMemo(() => readWeights(parentRun), [parentRun]);
  const initialMode = useMemo(
    () => readForecastMode(parentRun),
    [parentRun],
  );
  const [fot, setFot] = useState<number>(initial.fot);
  const [understaffing, setUnderstaffing] = useState<number>(
    initial.understaffing,
  );
  const [overtime, setOvertime] = useState<number>(initial.overtime);
  const [fairness, setFairness] = useState<number>(initial.fairness);
  const [forecastMode, setForecastMode] = useState<ForecastMode>(initialMode);
  const [errorText, setErrorText] = useState<string>('');

  const branch = useBranchOptimizationRun();

  const handleSubmit = async () => {
    setErrorText('');
    try {
      await branch.mutateAsync({
        parentRunId: parentRun.id,
        body: {
          weights: { fot, understaffing, overtime, fairness },
          forecast_mode: forecastMode,
        },
      });
      onClose();
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : 'Ошибка запуска');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-purple-600" />
              What-if: новый запуск от родителя
            </span>
          </DialogTitle>
          <DialogDescription>
            Те же сотрудники, период и калибровка — изменим только веса
            целевой функции и/или режим прогноза. Локацию пересобирать не
            нужно: snapshot родителя гарантирует воспроизводимость.
            {parentRun.position_ids && parentRun.position_ids.length > 0 && (
              <span className="block mt-1 text-amber-700">
                Scope наследуется: только {parentRun.position_ids.length}{' '}
                выбранных должностей. Чтобы расширить — запустите новый run
                без what-if.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="α (ФОТ)"
              hint="штраф за стоимость графика"
              value={fot}
              onChange={setFot}
            />
            <NumberField
              label="β (недокрытие)"
              hint="штраф за нехватку людей в час спроса"
              value={understaffing}
              onChange={setUnderstaffing}
            />
            <NumberField
              label="γ (переработка)"
              hint="штраф за часы сверх нормы"
              value={overtime}
              onChange={setOvertime}
            />
            <NumberField
              label="δ (равномерность)"
              hint="штраф за неравный счёт дней внутри должности"
              value={fairness}
              onChange={setFairness}
            />
          </div>

          <div>
            <Label>Режим прогноза</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {(
                [
                  { v: 'expected', l: 'Ожидаемый' },
                  { v: 'conservative', l: 'Осторожный' },
                  { v: 'optimistic', l: 'Оптимистичный' },
                ] as const
              ).map((o) => {
                const active = forecastMode === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForecastMode(o.v as ForecastMode)}
                    className={`border rounded-md px-2 py-1.5 text-sm ${
                      active
                        ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-300 text-purple-900'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
          </div>

          {errorText && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorText}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={branch.isPending}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={branch.isPending}>
            {branch.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Запуск...
              </>
            ) : (
              <>
                <GitBranch className="h-4 w-4 mr-2" />
                Запустить ветку
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NumberField: React.FC<{
  label: string;
  hint?: string;
  value: number;
  onChange: (n: number) => void;
}> = ({ label, hint, value, onChange }) => (
  <div>
    <Label>{label}</Label>
    <Input
      type="number"
      step="0.01"
      min={0}
      value={Number.isFinite(value) ? value : 0}
      onChange={(e) => {
        const v = e.target.valueAsNumber;
        onChange(Number.isFinite(v) ? v : 0);
      }}
    />
    {hint && (
      <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
    )}
  </div>
);
